import { useEffect, useState } from 'react'
import { Link, Navigate, useNavigate } from 'react-router-dom'
import { ApiError } from '../../api/client'
import { exportApi } from '../../api/exports'
import { Alert } from '../../components/Alert'
import { BbIcon } from '../../components/BbIcon'
import { BbStepper } from '../../components/BbStepper'
import { useCompanies } from '../../companies/useCompanies'
import { useExportWizard } from '../../export/ExportWizardContext'
import { EXPORT_STEPS } from './steps'

/* Step 4, ported from "batchbooks_export_step_4_export_result".

   The endpoint is real: submit queues a job and returns its id, which is held
   so the page can deep-link to that job rather than dropping the user on an
   unfiltered history list. A failed submit shows what the server said and the
   deep link is simply absent — no job id is ever invented. */
export function ExportDonePage() {
  const wizard = useExportWizard()
  const navigate = useNavigate()
  const { current } = useCompanies()
  const [serverSaid, setServerSaid] = useState<string | null>(null)
  const [jobId, setJobId] = useState<string | null>(null)

  const { entityType, reference, dateMode, preset, from, to } = wizard
  const companyId = current?.id ?? null

  useEffect(() => {
    if (!entityType) return
    let cancelled = false
    void exportApi
      .submit({
        qboCompanyId: companyId,
        entityType,
        reference: reference || undefined,
        dateMode,
        preset: dateMode === 'preset' ? preset : undefined,
        from: dateMode === 'custom' ? from || undefined : undefined,
        to: dateMode === 'custom' ? to || undefined : undefined,
      })
      .then((result) => {
        if (cancelled) return
        setServerSaid(null)
        setJobId(result.jobId)
      })
      .catch((cause: unknown) => {
        if (cancelled) return
        setServerSaid(
          cause instanceof ApiError ? `${cause.message}` : 'Could not reach the server.',
        )
      })
    return () => {
      cancelled = true
    }
  }, [entityType, companyId, reference, dateMode, preset, from, to])

  if (!entityType) return <Navigate to="/app/export" replace />

  const subject = wizard.entity?.label ?? entityType

  const startOver = () => {
    wizard.reset()
    navigate('/app/export')
  }

  return (
    <div className="bb bb-wizard">
      <BbStepper steps={EXPORT_STEPS} current={3} />

      {serverSaid ? (
        <Alert tone="warn" title="The server has not queued anything">
          {serverSaid}
        </Alert>
      ) : null}

      <div className="bb-success">
        <span className="bb-success-ico bb-success-ico-navy">
          <BbIcon name="check-circle" />
        </span>
        <h2>Export Job Queued</h2>
        <p>
          Your export job for <b>{subject}</b> is queued. Track its progress and download the
          finished file from its job page.
        </p>
      </div>

      <div className="bb-wizard-foot">
        <button className="bb-btn bb-btn-quiet" type="button" onClick={startOver}>
          Start New Export
        </button>
        <span className="bb-wizard-foot-r">
          <Link className="bb-btn bb-btn-navy" to={jobId ? `/app/jobs/${jobId}` : '/app/jobs'}>
            <BbIcon name="history" />
            {jobId ? 'View this job' : 'Go to Job History'}
          </Link>
        </span>
      </div>
    </div>
  )
}
